import { motion } from "motion/react";

export type TabItem<K extends string = string> = {
  key: K;
  label: string;
  /** Compteur optionnel affiché en badge. */
  count?: number;
  disabled?: boolean;
};

type Props<K extends string = string> = {
  value: K;
  onChange: (key: K) => void;
  items: ReadonlyArray<TabItem<K>>;
  /** Style "underline" (défaut) ou "segmented". */
  variant?: "underline" | "segmented";
};

export function Tabs<K extends string = string>({
  value,
  onChange,
  items,
  variant = "underline",
}: Props<K>) {
  if (variant === "segmented") {
    return (
      <div
        role="tablist"
        className="inline-flex rounded-xl bg-navy-50 p-1 ring-1 ring-navy-100"
      >
        {items.map((it) => {
          const active = it.key === value;
          return (
            <button
              key={it.key}
              role="tab"
              aria-selected={active}
              disabled={it.disabled}
              onClick={() => onChange(it.key)}
              className={`relative px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 ${
                active
                  ? "bg-white text-navy-800 shadow-sm"
                  : "text-navy-500 hover:text-navy-700"
              }`}
            >
              {it.label}
              {typeof it.count === "number" ? (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-5 px-1.5 py-0.5 rounded-full bg-navy-100 text-[10px] font-bold text-navy-700">
                  {it.count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="tablist"
      className="flex flex-wrap gap-1 border-b border-navy-100"
    >
      {items.map((it) => {
        const active = it.key === value;
        return (
          <button
            key={it.key}
            role="tab"
            aria-selected={active}
            disabled={it.disabled}
            onClick={() => onChange(it.key)}
            className={`relative px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${
              active
                ? "text-navy-800"
                : "text-navy-500 hover:text-navy-700"
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              {it.label}
              {typeof it.count === "number" ? (
                <span className="inline-flex items-center justify-center min-w-5 px-1.5 py-0.5 rounded-full bg-navy-100 text-[10px] font-bold text-navy-700">
                  {it.count}
                </span>
              ) : null}
            </span>
            {active ? (
              <motion.span
                layoutId="tab-underline"
                className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-accent-500"
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
